/**
 * People → User groups (Slack), all over UserGroupsViewModel: the list (Active / Disabled tabs, search, rows with
 * name, @handle, description, member count and "You're a member"), Create / Edit (name, handle suggested from the
 * name, description, members, default channels), Disable / Enable, and "Who can manage user groups" for owners and
 * admins. `UserGroupSheets` is the editor sheet and the group card (clicking a group mention or a row); the member
 * card host mounts it once for the whole shell (MemberCard.tsx), and the settings section mounts it itself when no
 * host is around. The view model is shared per workspace (`useUserGroups`).
 */
import {
  Profile,
  Search,
  UserGroupsViewModel,
  type UserGroupEditor,
  type UserGroupsState,
} from "@bundle/core";
import type { UserGroupPolicy } from "@bundle/contracts";
import { useCallback, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Chip,
  Icon,
  IconButton,
  Menu,
  Pressable,
  RadioGroup,
  SegmentedControl,
  Sheet,
  Stack,
  Text,
  TextField,
  type MenuEntry,
} from "../../primitives/index.ts";
import { useOwned, useViewModel, useWorkspace } from "../../hooks/index.ts";
import { useT, useTr } from "../../i18n/I18nProvider.tsx";
import { useGroupCard } from "../../context/SettingsContext.tsx";
import {
  FieldLabel,
  FieldNote,
  SettingsNotice,
  SettingsSection,
} from "./SettingsKit.tsx";

/** The workspace's user groups view model (one per workspace, shared by settings and the group card). */
export function useUserGroups(): {
  vm: UserGroupsViewModel;
  state: UserGroupsState;
} {
  const ws = useWorkspace();
  const create = useCallback(() => new UserGroupsViewModel(ws.vm), [ws.vm]);
  const vm = useOwned(`usergroups:${ws.vm.wsId}`, create);
  const state = useViewModel(vm);
  return { vm, state };
}

const PICK_LIMIT = 8;

export function UserGroupsSettings({
  vm,
  state: s,
}: {
  vm: UserGroupsViewModel;
  state: UserGroupsState;
}) {
  const t = useT();
  const host = useGroupCard();
  const empty =
    s.query.trim() !== ""
      ? t("userGroups.emptySearch")
      : s.filter === "active"
      ? t("userGroups.empty")
      : t("userGroups.emptyDisabled");
  return (
    <Stack gap="6" testID="settings-usergroups">
      <SettingsNotice
        notice={s.error}
        onClose={() => vm.dismissError()}
        testID="usergroups-notice"
      />
      <Stack direction="horizontal" align="center" gap="3">
        <Stack grow>
          <Text variant="footnote" color="text.secondary">
            {t("userGroups.intro")}
          </Text>
        </Stack>
        {s.canManage ? (
          <Button
            variant="primary"
            icon="plus"
            label={t("userGroups.create")}
            onPress={() => vm.openCreate()}
            testID="usergroups-create"
          />
        ) : null}
      </Stack>
      <Stack gap="3">
        <TextField
          value={s.query}
          onChange={(v) => vm.setQuery(v)}
          placeholder={t("userGroups.search")}
          leadingIcon="search"
          testID="usergroups-search"
        />
        <SegmentedControl
          variant="tabs"
          accessibilityLabel={t("settings.nav.userGroups")}
          value={s.filter}
          onChange={(f) => vm.setFilter(f)}
          options={[
            {
              value: "active",
              label: t("userGroups.tab.active"),
              count: s.counts.active,
            },
            {
              value: "disabled",
              label: t("userGroups.tab.disabled"),
              count: s.counts.disabled,
            },
          ]}
          testID="usergroups-tabs"
        />
        <Stack gap="0.5" testID="usergroups-list">
          {s.rows.length === 0 ? (
            <FieldNote text={empty} testID="usergroups-empty" />
          ) : null}
          {s.rows.map((row) => {
            const menu: MenuEntry[] = [
              {
                id: "edit",
                label: t("userGroups.edit"),
                icon: "edit",
                onSelect: () => vm.openEdit(row.id),
              },
              row.disabled
                ? {
                    id: "enable",
                    label: t("userGroups.enable"),
                    icon: "checkCircle",
                    onSelect: () => void vm.setDisabled(row.id, false),
                  }
                : {
                    id: "disable",
                    label: t("userGroups.disable"),
                    icon: "ban",
                    destructive: true,
                    onSelect: () => void vm.setDisabled(row.id, true),
                  },
            ];
            return (
              <Stack
                key={row.id}
                direction="horizontal"
                align="center"
                gap="2"
                testID={`usergroup-row-${row.id}`}
              >
                <Stack grow>
                  <Pressable
                    direction="horizontal"
                    radius="md"
                    onPress={() => vm.openCard(row.id)}
                    accessibilityLabel={`${row.name} ${row.handle}`}
                    testID={`usergroup-open-${row.id}`}
                  >
                    <Stack
                      grow
                      direction="horizontal"
                      align="center"
                      gap="3"
                      paddingX="2"
                      paddingY="2"
                    >
                      <Stack
                        width="avatar.md"
                        height="avatar.md"
                        radius="md"
                        background="chip.bg"
                        align="center"
                        justify="center"
                      >
                        <Icon
                          name="users"
                          size="icon.sm"
                          color="text.secondary"
                        />
                      </Stack>
                      <Stack grow gap="0.5">
                        <Stack
                          direction="horizontal"
                          align="center"
                          gap="1.5"
                          wrap
                        >
                          <Text
                            weight="semibold"
                            lines={1}
                            color={
                              row.disabled ? "text.tertiary" : "text.primary"
                            }
                          >
                            {row.name}
                          </Text>
                          <Text
                            variant="footnote"
                            color="text.secondary"
                            lines={1}
                          >
                            {row.handle}
                          </Text>
                          {row.mine ? (
                            <Chip label={t("userGroups.mine")} tone="accent" />
                          ) : null}
                          {row.disabled ? (
                            <Chip label={t("userGroups.disabledBadge")} />
                          ) : null}
                        </Stack>
                        <Text
                          variant="caption"
                          color="text.secondary"
                          lines={1}
                        >
                          {[
                            t("userGroups.memberCount", {
                              count: row.memberCount,
                            }),
                            row.description,
                          ]
                            .filter((x) => x !== undefined && x !== "")
                            .join(" · ")}
                        </Text>
                      </Stack>
                    </Stack>
                  </Pressable>
                </Stack>
                {s.canManage ? (
                  <Menu
                    items={menu}
                    align="end"
                    testID={`usergroup-menu-${row.id}`}
                  >
                    <IconButton
                      icon="more"
                      accessibilityLabel={t("userGroups.actions", {
                        name: row.name,
                      })}
                      onPress={() => undefined}
                      testID={`usergroup-more-${row.id}`}
                    />
                  </Menu>
                ) : null}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
      {s.canSetPolicy ? (
        <SettingsSection
          title={t("userGroups.policy.label")}
          testID="usergroups-policy"
        >
          <RadioGroup<UserGroupPolicy>
            accessibilityLabel={t("userGroups.policy.label")}
            value={s.policy}
            onChange={(p) => void vm.setPolicy(p)}
            options={[
              { value: "everyone", label: t("userGroups.policy.everyone") },
              { value: "admins", label: t("userGroups.policy.admins") },
            ]}
            testID="usergroups-policy-radio"
          />
        </SettingsSection>
      ) : (
        <FieldNote
          text={`${t("userGroups.policy.label")}: ${
            s.policy === "everyone"
              ? t("userGroups.policy.everyone")
              : t("userGroups.policy.admins")
          }`}
          testID="usergroups-policy-readonly"
        />
      )}
      {host === null ? <UserGroupSheets vm={vm} state={s} /> : null}
    </Stack>
  );
}

export type UserGroupSheetsProps = {
  vm: UserGroupsViewModel;
  state: UserGroupsState;
  /** A member of the group was clicked on the card (their profile card). */
  onOpenMember?: (userId: string) => void;
  /** A default channel was clicked on the card. */
  onOpenChannel?: (channelId: string) => void;
};

/** The create / edit sheet and the group card. */
export function UserGroupSheets({
  vm,
  state: s,
  onOpenMember,
  onOpenChannel,
}: UserGroupSheetsProps) {
  return (
    <>
      <EditorSheet vm={vm} editor={s.editor} />
      <GroupCardSheet
        vm={vm}
        state={s}
        onOpenMember={onOpenMember}
        onOpenChannel={onOpenChannel}
      />
    </>
  );
}

function EditorSheet({
  vm,
  editor: e,
}: {
  vm: UserGroupsViewModel;
  editor: UserGroupEditor | undefined;
}) {
  const t = useT();
  return (
    <Sheet
      open={e !== undefined}
      onClose={() => vm.closeEditor()}
      title={t(e?.id === undefined ? "userGroups.create" : "userGroups.edit")}
      closeLabel={t("common.close")}
      testID="usergroup-editor"
    >
      {e === undefined ? null : <EditorForm vm={vm} e={e} />}
    </Sheet>
  );
}

function EditorForm({
  vm,
  e,
}: {
  vm: UserGroupsViewModel;
  e: UserGroupEditor;
}) {
  const t = useT();
  const tr = useTr();
  const ws = useWorkspace();
  const cache = ws.state.cache;
  const [memberQuery, setMemberQuery] = useState("");
  const [channelQuery, setChannelQuery] = useState("");
  const members = useMemo(() => cache?.members ?? {}, [cache?.members]);
  const channels = useMemo(() => cache?.channels ?? {}, [cache?.channels]);
  // People (active humans; agents can't be in groups) and channels (public, or private ones I'm in) to add.
  const memberCandidates = useMemo(() => {
    if (memberQuery.trim() === "") return [];
    return Object.values(members)
      .filter(
        (m) =>
          m.kind === "human" &&
          !m.deactivated &&
          !e.memberIds.includes(m.userId)
      )
      .map((m) => ({
        m,
        score: Math.max(
          Search.matchScore(memberQuery, m.displayName),
          Search.matchScore(memberQuery, m.fullName),
          m.handle === undefined ? 0 : Search.matchScore(memberQuery, m.handle)
        ),
      }))
      .filter((x) => x.score > 0)
      .toSorted(
        (a, b) =>
          b.score - a.score || a.m.displayName.localeCompare(b.m.displayName)
      )
      .slice(0, PICK_LIMIT)
      .map((x) => x.m);
  }, [members, memberQuery, e.memberIds]);
  const channelCandidates = useMemo(() => {
    if (channelQuery.trim() === "") return [];
    return Object.values(channels)
      .filter(
        (c) =>
          !c.archived &&
          !e.channelIds.includes(c.id) &&
          (c.kind === "public" ||
            (c.kind === "private" && cache?.memberships[c.id] !== undefined))
      )
      .map((c) => ({ c, score: Search.matchScore(channelQuery, c.name) }))
      .filter((x) => x.score > 0)
      .toSorted((a, b) => b.score - a.score || a.c.name.localeCompare(b.c.name))
      .slice(0, PICK_LIMIT)
      .map((x) => x.c);
  }, [channels, channelQuery, e.channelIds, cache?.memberships]);

  return (
    <Stack gap="4" padding="4">
      <TextField
        label={t("userGroups.field.name")}
        value={e.name}
        onChange={(v) => vm.setName(v)}
        placeholder={t("userGroups.field.namePlaceholder")}
        autoFocus
        testID="usergroup-name"
      />
      <TextField
        label={t("userGroups.field.handle")}
        value={e.handle}
        onChange={(v) => vm.setHandle(v)}
        leadingIcon="at"
        helperText={t("userGroups.field.handleHelp")}
        testID="usergroup-handle"
      />
      <TextField
        label={t("userGroups.field.description")}
        value={e.description}
        onChange={(v) => vm.setDescription(v)}
        multiline
        testID="usergroup-description"
      />

      <Stack gap="2" testID="usergroup-members">
        <FieldLabel label={t("userGroups.field.members")} />
        {e.memberIds.length === 0 ? null : (
          <Stack direction="horizontal" gap="1.5" wrap>
            {e.memberIds.map((id) => {
              const m = members[id];
              const name = m?.displayName ?? id;
              return (
                <Chip
                  key={id}
                  label={name}
                  icon="user"
                  onRemove={() => vm.toggleMember(id)}
                  testID={`usergroup-member-${id}`}
                />
              );
            })}
          </Stack>
        )}
        <TextField
          value={memberQuery}
          onChange={setMemberQuery}
          placeholder={t("userGroups.field.membersSearch")}
          leadingIcon="search"
          testID="usergroup-members-search"
        />
        <Candidates
          query={memberQuery}
          items={memberCandidates.map((m) => {
            const look = Profile.avatarLook(m);
            return {
              id: m.userId,
              label: m.displayName,
              leading: (
                <Avatar
                  name={m.displayName}
                  src={look.src}
                  tone={look.tone}
                  emoji={look.emoji}
                  size="avatar.xs"
                />
              ),
              onPick: () => {
                vm.toggleMember(m.userId);
                setMemberQuery("");
              },
            };
          })}
          testID="usergroup-member-candidate"
        />
      </Stack>

      <Stack gap="2" testID="usergroup-channels">
        <FieldLabel label={t("userGroups.field.channels")} />
        <FieldNote text={t("userGroups.field.channelsHelp")} />
        {e.channelIds.length === 0 ? null : (
          <Stack direction="horizontal" gap="1.5" wrap>
            {e.channelIds.map((id) => {
              const c = channels[id];
              return (
                <Chip
                  key={id}
                  label={c?.name ?? id}
                  icon={c?.kind === "private" ? "lock" : "hash"}
                  onRemove={() => vm.toggleChannel(id)}
                  testID={`usergroup-channel-${id}`}
                />
              );
            })}
          </Stack>
        )}
        <TextField
          value={channelQuery}
          onChange={setChannelQuery}
          placeholder={t("userGroups.field.channelsSearch")}
          leadingIcon="search"
          testID="usergroup-channels-search"
        />
        <Candidates
          query={channelQuery}
          items={channelCandidates.map((c) => ({
            id: c.id,
            label: c.name,
            leading: (
              <Icon
                name={c.kind === "private" ? "lock" : "hash"}
                size="icon.sm"
                color="text.secondary"
              />
            ),
            onPick: () => {
              vm.toggleChannel(c.id);
              setChannelQuery("");
            },
          }))}
          testID="usergroup-channel-candidate"
        />
      </Stack>

      {e.problem === undefined ? null : (
        <FieldNote
          text={tr(e.problem)}
          tone="danger"
          testID="usergroup-problem"
        />
      )}
      <Stack direction="horizontal" gap="2" justify="end">
        <Button
          variant="ghost"
          label={t("common.cancel")}
          onPress={() => vm.closeEditor()}
          testID="usergroup-cancel"
        />
        <Button
          variant="primary"
          label={e.id === undefined ? t("userGroups.create") : t("common.save")}
          loading={e.saving}
          onPress={() => void vm.save()}
          testID="usergroup-save"
        />
      </Stack>
    </Stack>
  );
}

function Candidates({
  query,
  items,
  testID,
}: {
  query: string;
  items: {
    id: string;
    label: string;
    leading: React.ReactNode;
    onPick: () => void;
  }[];
  testID: string;
}) {
  const t = useT();
  if (query.trim() === "") return null;
  if (items.length === 0)
    return (
      <FieldNote
        text={t("userGroups.field.noMatches")}
        testID={`${testID}-none`}
      />
    );
  return (
    <Stack gap="0.5">
      {items.map((it) => (
        <Pressable
          key={it.id}
          direction="horizontal"
          radius="md"
          onPress={it.onPick}
          testID={`${testID}-${it.id}`}
        >
          <Stack
            grow
            direction="horizontal"
            align="center"
            gap="2"
            paddingX="2"
            paddingY="1"
          >
            {it.leading}
            <Stack grow>
              <Text variant="footnote" lines={1}>
                {it.label}
              </Text>
            </Stack>
            <Icon name="plus" size="icon.sm" color="text.tertiary" />
          </Stack>
        </Pressable>
      ))}
    </Stack>
  );
}

/** The group's card: name, @handle, description, members, default channels, "Join N default channels", Edit. */
function GroupCardSheet({
  vm,
  state: s,
  onOpenMember,
  onOpenChannel,
}: UserGroupSheetsProps) {
  const t = useT();
  const ws = useWorkspace();
  const cache = ws.state.cache;
  const card = s.card;
  const [joining, setJoining] = useState(false);
  return (
    <Sheet
      open={card !== undefined}
      onClose={() => vm.closeCard()}
      title={
        card === undefined ? "" : t("userGroups.cardTitle", { name: card.name })
      }
      closeLabel={t("common.close")}
      testID="group-card"
    >
      {card === undefined ? null : (
        <Stack gap="4">
          <Stack direction="horizontal" align="center" gap="4">
            <Stack
              width="avatar.xl"
              height="avatar.xl"
              radius="lg"
              background="chip.bg"
              align="center"
              justify="center"
            >
              <Icon name="users" size="icon.lg" color="text.secondary" />
            </Stack>
            <Stack grow gap="0.5">
              <Text
                variant="title3"
                weight="bold"
                lines={1}
                testID="group-card-name"
              >
                {card.name}
              </Text>
              <Text color="text.secondary" lines={1} testID="group-card-handle">
                {card.handle}
              </Text>
            </Stack>
          </Stack>
          {card.description === undefined ? null : (
            <Text
              color="text.secondary"
              selectable
              testID="group-card-description"
            >
              {card.description}
            </Text>
          )}
          {card.disabled ? (
            <Stack
              direction="horizontal"
              align="center"
              gap="2"
              padding="2"
              radius="md"
              background="bg.hover"
              testID="group-card-disabled"
            >
              <Icon name="ban" size="icon.sm" color="text.tertiary" />
              <Stack grow>
                <Text variant="footnote" color="text.secondary">
                  {t("userGroups.card.disabled")}
                </Text>
              </Stack>
            </Stack>
          ) : null}
          <Stack gap="1" testID="group-card-members">
            <FieldLabel
              label={`${t("userGroups.card.members")} · ${
                card.memberIds.length
              }`}
            />
            {card.memberIds.length === 0 ? (
              <FieldNote text={t("userGroups.card.noMembers")} />
            ) : null}
            {card.memberIds.map((id) => {
              const m = cache?.members[id];
              if (m === undefined) return null;
              const look = Profile.avatarLook(m);
              return (
                <Pressable
                  key={id}
                  direction="horizontal"
                  radius="md"
                  onPress={() => onOpenMember?.(id)}
                  disabled={onOpenMember === undefined}
                  testID={`group-card-member-${id}`}
                >
                  <Stack
                    grow
                    direction="horizontal"
                    align="center"
                    gap="2"
                    paddingX="1"
                    paddingY="1"
                  >
                    <Avatar
                      name={m.displayName}
                      src={look.src}
                      tone={look.tone}
                      emoji={look.emoji}
                      size="avatar.sm"
                    />
                    <Stack grow>
                      <Text variant="footnote" lines={1}>
                        {m.displayName}
                      </Text>
                    </Stack>
                    {m.title === undefined ? null : (
                      <Text variant="caption" color="text.tertiary" lines={1}>
                        {m.title}
                      </Text>
                    )}
                  </Stack>
                </Pressable>
              );
            })}
          </Stack>
          {card.channelIds.length === 0 ? null : (
            <Stack gap="1" testID="group-card-channels">
              <FieldLabel label={t("userGroups.card.channels")} />
              {card.channelIds.map((id) => {
                const c = cache?.channels[id];
                if (c === undefined) return null;
                return (
                  <Pressable
                    key={id}
                    direction="horizontal"
                    radius="md"
                    onPress={() => onOpenChannel?.(id)}
                    disabled={onOpenChannel === undefined}
                    testID={`group-card-channel-${id}`}
                  >
                    <Stack
                      grow
                      direction="horizontal"
                      align="center"
                      gap="2"
                      paddingX="1"
                      paddingY="1"
                    >
                      <Icon
                        name={c.kind === "private" ? "lock" : "hash"}
                        size="icon.sm"
                        color="text.secondary"
                      />
                      <Stack grow>
                        <Text variant="footnote" lines={1}>
                          {c.name}
                        </Text>
                      </Stack>
                    </Stack>
                  </Pressable>
                );
              })}
            </Stack>
          )}
          <Stack direction="horizontal" gap="2" wrap>
            {card.joinableChannelIds.length > 0 ? (
              <Button
                variant="primary"
                icon="plus"
                label={t("userGroups.card.join", {
                  count: card.joinableChannelIds.length,
                })}
                loading={joining}
                onPress={() => {
                  setJoining(true);
                  void vm
                    .joinChannels(card.id)
                    .finally(() => setJoining(false));
                }}
                testID="group-card-join"
              />
            ) : null}
            {card.canEdit ? (
              <Button
                variant="secondary"
                icon="edit"
                label={t("userGroups.edit")}
                onPress={() => {
                  const id = card.id;
                  vm.closeCard();
                  vm.openEdit(id);
                }}
                testID="group-card-edit"
              />
            ) : null}
          </Stack>
        </Stack>
      )}
    </Sheet>
  );
}
